import { initializeApp } from 'firebase/app';
import { connectAuthEmulator, getAuth, getReactNativePersistence, initializeAuth } from 'firebase/auth';
import {
  getFirestore,
  initializeFirestore,
  connectFirestoreEmulator,
  memoryLocalCache,
  persistentLocalCache,
  persistentSingleTabManager,
} from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getPerformance } from 'firebase/performance';
import { Platform } from 'react-native';
import Constants from 'expo-constants';
import * as Updates from 'expo-updates';
import AsyncStorage from '@react-native-async-storage/async-storage';

type FirebaseClientConfig = {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
};

type ExpoExtra = Record<string, unknown> | undefined;

/** Accepts Expo extra booleans/strings and CI env quirks (`1`, `yes`). */
const readTruthyExtra = (raw: unknown): boolean =>
  raw === true || String(raw ?? '').trim().toLowerCase() === 'true' || String(raw ?? '').trim() === '1' || String(raw ?? '').trim().toLowerCase() === 'yes';

/** CI shells often export `VAR=` (empty). Treat as unset so app.json / manifest fallbacks win. */
const isBlankConfigValue = (raw: unknown): boolean =>
  raw === undefined || raw === null || (typeof raw === 'string' && raw.trim() === '');

const readPresentEnv = (key: string): string | undefined => {
  const v = process.env[key];
  return isBlankConfigValue(v) ? undefined : String(v);
};

const isValidFirebaseClientConfig = (cfg: any): cfg is FirebaseClientConfig => {
  return Boolean(
    cfg &&
    typeof cfg.apiKey === 'string' &&
    typeof cfg.authDomain === 'string' &&
    typeof cfg.projectId === 'string' &&
    typeof cfg.storageBucket === 'string' &&
    typeof cfg.messagingSenderId === 'string' &&
    typeof cfg.appId === 'string',
  );
};

/**
 * Expo exposes manifest/config data differently depending on runtime:
 * - Expo Go / Metro: manifest / manifest2
 * - Dev client / embedded config: expoConfig
 * - expo-updates launches: Updates.manifest may be the only populated source
 *
 * Read from all known locations so release/OTA/native startup does not fail
 * just because one manifest shape is empty.
 */
const expoExtraCandidates: ExpoExtra[] = (() => {
  const c: any = Constants as any;
  const updatesManifest: any = Updates.manifest as any;

  return [
    Constants.expoConfig?.extra as ExpoExtra,
    c?.expoConfig?.extra as ExpoExtra,
    c?.manifest?.extra as ExpoExtra,
    c?.manifest2?.extra?.expoClient?.extra as ExpoExtra,
    c?.manifest2?.extra as ExpoExtra,
    updatesManifest?.extra?.expoClient?.extra as ExpoExtra,
    updatesManifest?.extra as ExpoExtra,
    c?.easConfig?.extra as ExpoExtra,
  ];
})();

/** First non-null `extra` object (backward-compatible with older single-manifest reads). */
const getExpoExtra = (): ExpoExtra => expoExtraCandidates.find((value) => value && typeof value === 'object');

/**
 * Prefer this for feature flags exported through `extra`.
 * RN Web static exports sometimes populate one manifest tier incompletely — the classic `.find()`
 * hides keys that exist on a deeper manifest. Walk in priority order; first **non-blank** value wins.
 * Blank strings count as unset so an empty tier does not shadow `app.json` defaults.
 */
const pickDefinedExpoExtraValue = (key: string): unknown => {
  for (const extra of expoExtraCandidates) {
    if (!extra || typeof extra !== 'object') continue;
    if (!Object.prototype.hasOwnProperty.call(extra, key)) continue;
    const v = (extra as Record<string, unknown>)[key];
    if (isBlankConfigValue(v)) continue;
    return v;
  }
  return undefined;
};

const expoExtra = getExpoExtra();
const extraFirebaseConfig = expoExtra?.firebaseConfig;

const envFirebaseConfig: Partial<FirebaseClientConfig> = {
  apiKey: process.env.EXPO_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.EXPO_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.EXPO_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.EXPO_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.EXPO_PUBLIC_FIREBASE_APP_ID,
  measurementId: process.env.EXPO_PUBLIC_FIREBASE_MEASUREMENT_ID,
};

// IMPORTANT: Avoid "mixed project" config (e.g. apiKey from one project, projectId from another).
// On native builds, prefer app.json/expo config extra when provided to prevent local env drift.
// On web, prefer env vars when set (CI/CD). Static `expo export -p web` / Firebase predeploy often has no
// EXPO_PUBLIC_* in the shell — fall back to embedded `extra.firebaseConfig` or the app will throw at import
// (blank white page).
const testFallbackFirebaseConfig: FirebaseClientConfig = {
  apiKey: 'test-api-key',
  authDomain: 'test.firebaseapp.com',
  projectId: 'test-project',
  storageBucket: 'test.appspot.com',
  messagingSenderId: '000000000000',
  appId: '1:000000000000:web:test',
};

const firebaseConfig: FirebaseClientConfig = (() => {
  // Native: prefer embedded extra first so local .env can't accidentally mix projects with the built plist.
  if (Platform.OS !== 'web' && isValidFirebaseClientConfig(extraFirebaseConfig)) {
    return extraFirebaseConfig;
  }
  if (isValidFirebaseClientConfig(envFirebaseConfig)) {
    return envFirebaseConfig as FirebaseClientConfig;
  }
  // Web (and any platform): Metro / expo export often has no EXPO_PUBLIC_FIREBASE_* in the shell — use app.config/app.json extra.
  if (isValidFirebaseClientConfig(extraFirebaseConfig)) {
    return extraFirebaseConfig;
  }
  if (process.env.NODE_ENV === 'test') {
    return testFallbackFirebaseConfig;
  }
  console.error('[Firebase Config] Missing client configuration sources', {
    platform: Platform.OS,
    hasExpoConfigExtra: Boolean((Constants.expoConfig?.extra as any)?.firebaseConfig),
    hasResolvedExpoExtra: Boolean(expoExtra?.firebaseConfig),
    hasManifestExtra: Boolean((Constants as any)?.manifest?.extra?.firebaseConfig),
    hasManifest2ExpoClientExtra: Boolean((Constants as any)?.manifest2?.extra?.expoClient?.extra?.firebaseConfig),
    hasManifest2Extra: Boolean((Constants as any)?.manifest2?.extra?.firebaseConfig),
    hasUpdatesManifestExtra: Boolean((Updates.manifest as any)?.extra?.firebaseConfig),
    hasUpdatesManifestExpoClientExtra: Boolean((Updates.manifest as any)?.extra?.expoClient?.extra?.firebaseConfig),
    hasEnvFirebaseConfig: isValidFirebaseClientConfig(envFirebaseConfig),
    updatesChannel: Updates.channel ?? null,
    updatesIsEmbeddedLaunch: Updates.isEmbeddedLaunch,
    updatesUpdateId: Updates.updateId ?? null,
  });
  throw new Error(
    '[Firebase] Missing Firebase client configuration. Set Expo extra.firebaseConfig or EXPO_PUBLIC_FIREBASE_* env vars.'
  );
})();

// Log which project we're using (only in dev mode)
if (__DEV__) {
  // Use warn so it shows even if console.log is stripped in native builds.
  console.warn('🔥 Firebase initialized with config:', {
    projectId: firebaseConfig.projectId,
    authDomain: firebaseConfig.authDomain,
    storageBucket: firebaseConfig.storageBucket,
    messagingSenderId: firebaseConfig.messagingSenderId,
    appId: firebaseConfig.appId,
    // Avoid leaking full API key; log just a prefix for debugging mismatches.
    apiKeyPrefix: firebaseConfig.apiKey?.slice?.(0, 8) ?? null,
    source:
      Platform.OS !== 'web' && isValidFirebaseClientConfig(extraFirebaseConfig)
        ? 'expo.extra.firebaseConfig'
        : isValidFirebaseClientConfig(envFirebaseConfig)
          ? 'env'
          : isValidFirebaseClientConfig(extraFirebaseConfig)
            ? 'expo.extra.firebaseConfig'
            : process.env.NODE_ENV === 'test'
              ? 'test-fallback'
              : 'missing',
  });
}

const app = initializeApp(firebaseConfig);

// App Check on web: use reCAPTCHA (standard v3 or Enterprise per Console). Native avoids JS App Check here.
const appCheckSiteKey = String(
  pickDefinedExpoExtraValue('EXPO_PUBLIC_APP_CHECK_RECAPTCHA_SITE_KEY') ??
    readPresentEnv('EXPO_PUBLIC_APP_CHECK_RECAPTCHA_SITE_KEY') ??
    '',
).trim();

/**
 * Web App Check: when a site key is present we initialize on production web by default, because
 * Firebase Console often **enforces** App Check on Firestore/Functions — without a client token every
 * request returns `permission-denied` (trips list, inbox callables, etc.).
 *
 * Set `EXPO_PUBLIC_APP_CHECK_RECAPTCHA_SITE_KEY` (+ optional Enterprise flag below). For local web,
 * App Check stays off (__DEV__) to avoid reCAPTCHA friction.
 *
 * Escape hatch if registration is broken (400 / throttle): `EXPO_PUBLIC_DISABLE_WEB_APP_CHECK=true`
 * (only if you also relax or disable enforcement in Firebase Console).
 *
 * Enterprise vs classic reCAPTCHA (must match Firebase App Check Console):
 * - Prefer embedding `EXPO_PUBLIC_APP_CHECK_USE_RECAPTCHA_ENTERPRISE=true` in Expo `extra`.
 * - **Web fallback:** Expo static export has been observed omitting this key from Constants even when it is
 *   present during `app.config` resolution — if the flag is missing but a site key exists, default to Enterprise.
 * - Use `EXPO_PUBLIC_APP_CHECK_USE_RECAPTCHA_ENTERPRISE=false` to force classic v3 explicitly.
 */
const webAppCheckExplicitlyDisabled = readTruthyExtra(
  pickDefinedExpoExtraValue('EXPO_PUBLIC_DISABLE_WEB_APP_CHECK') ?? readPresentEnv('EXPO_PUBLIC_DISABLE_WEB_APP_CHECK'),
);

/** Must match Firebase Console → App Check → Web app attestation provider (Enterprise vs standard reCAPTCHA v3). */
const useRecaptchaEnterpriseProvider = (() => {
  const fromExtra = pickDefinedExpoExtraValue('EXPO_PUBLIC_APP_CHECK_USE_RECAPTCHA_ENTERPRISE');
  const fromEnv = readPresentEnv('EXPO_PUBLIC_APP_CHECK_USE_RECAPTCHA_ENTERPRISE');
  const raw = !isBlankConfigValue(fromExtra) ? fromExtra : fromEnv;
  if (raw !== undefined && raw !== null) {
    return readTruthyExtra(raw);
  }
  if (Platform.OS !== 'web') return false;
  if (!appCheckSiteKey.length) return false;
  return true;
})();

const shouldEnableWebAppCheck =
  appCheckSiteKey.length > 0 &&
  Platform.OS === 'web' &&
  typeof window !== 'undefined' &&
  !__DEV__ &&
  !webAppCheckExplicitlyDisabled;

if (
  webAppCheckExplicitlyDisabled &&
  appCheckSiteKey.length > 0 &&
  Platform.OS === 'web' &&
  typeof window !== 'undefined'
) {
  console.warn(
    '[Firebase] Web App Check disabled via EXPO_PUBLIC_DISABLE_WEB_APP_CHECK. Fix Firebase App Check registration, then remove the flag.',
  );
}

if (appCheckSiteKey.length > 0 && Platform.OS === 'web' && typeof window !== 'undefined' && !shouldEnableWebAppCheck && __DEV__) {
  console.warn('[Firebase] App Check disabled for local web development to avoid reCAPTCHA auth delays on localhost.');
}

if (shouldEnableWebAppCheck) {
  try {
    const {
      initializeAppCheck,
      ReCaptchaV3Provider,
      ReCaptchaEnterpriseProvider,
    } = require('firebase/app-check');
    const Provider = useRecaptchaEnterpriseProvider ? ReCaptchaEnterpriseProvider : ReCaptchaV3Provider;
    if (__DEV__) {
      console.warn(
        `[Firebase] App Check web provider: ${useRecaptchaEnterpriseProvider ? 'ReCaptchaEnterprise' : 'ReCaptchaV3'}`,
      );
    }
    initializeAppCheck(app, {
      provider: new Provider(appCheckSiteKey),
      isTokenAutoRefreshEnabled: true,
    });
    if (!__DEV__) {
      // One-shot signal for verifying prod bundles (expect App Check REST: exchangeRecaptchaEnterpriseToken vs exchangeRecaptchaV3Token).
      console.info('[Firebase] Web App Check initialized', {
        provider: useRecaptchaEnterpriseProvider ? 'ReCaptchaEnterprise' : 'ReCaptchaV3',
        siteKeyPrefix: `${appCheckSiteKey.slice(0, 12)}…`,
      });
    }
  } catch (e) {
    if (__DEV__) {
      console.warn('[Firebase] App Check init skipped:', (e as Error)?.message);
    }
  }
}

export const firebaseApp = app;

// Firestore transport (web): only force HTTP long polling when explicitly enabled.
// Legacy default `experimentalForceLongPolling: true` helps a few locked-down proxies, but Safari/WebKit
// often rejects the downstream `Listen/channel` requests ("Fetch API cannot load ... due to access control checks"),
// which then surfaces as flaky `permission-denied`/listener errors alongside cache hydrations.
//
// Opt back in globally: Expo extra `EXPO_PUBLIC_FIRESTORE_WEB_FORCE_LONG_POLLING=true`
// or build-time `process.env.EXPO_PUBLIC_FIRESTORE_WEB_FORCE_LONG_POLLING=true`.
const webFirestoreLongPollForced = (() => {
  if (Platform.OS !== 'web') return false;
  return readTruthyExtra(
    pickDefinedExpoExtraValue('EXPO_PUBLIC_FIRESTORE_WEB_FORCE_LONG_POLLING') ??
      readPresentEnv('EXPO_PUBLIC_FIRESTORE_WEB_FORCE_LONG_POLLING'),
  );
})();

/**
 * RN WebChannel streams can wedge on mobile networks (especially Android).
 * Firebase recommends long-polling in some setups. Escape hatch via extra/env.
 * Accepts legacy `EXPO_PUBLIC_FIRESTORE_IOS_DISABLE_FORCE_LONG_POLLING` for older builds.
 */
const nativeFirestoreDisableForceLongPolling = readTruthyExtra(
  pickDefinedExpoExtraValue('EXPO_PUBLIC_FIRESTORE_NATIVE_DISABLE_FORCE_LONG_POLLING') ??
    pickDefinedExpoExtraValue('EXPO_PUBLIC_FIRESTORE_IOS_DISABLE_FORCE_LONG_POLLING') ??
    readPresentEnv('EXPO_PUBLIC_FIRESTORE_NATIVE_DISABLE_FORCE_LONG_POLLING') ??
    readPresentEnv('EXPO_PUBLIC_FIRESTORE_IOS_DISABLE_FORCE_LONG_POLLING'),
);

// React Native: initialize Auth with AsyncStorage persistence
// so callable Functions/Firestore reliably receive the user token.
export const auth =
  Platform.OS === 'web'
    ? getAuth(app)
    : (() => {
      try {
        return initializeAuth(app, {
          persistence: getReactNativePersistence(AsyncStorage),
        });
      } catch (e: any) {
        // initializeAuth throws if called more than once (fast refresh / module re-eval).
        if (__DEV__) {
          console.log('[Firebase] initializeAuth already called; falling back to getAuth()', e?.message);
        }
        return getAuth(app);
      }
    })();
export const db: ReturnType<typeof getFirestore> = (() => {
  if (Platform.OS === 'web') {
    // Prefer durable IndexedDB persistence on web. If the browser blocks the
    // persistence APIs we need, fall back to in-memory cache (session-only).
    try {
      return initializeFirestore(app, {
        localCache: persistentLocalCache({
          tabManager: persistentSingleTabManager(undefined),
        }),
        ...(webFirestoreLongPollForced ? { experimentalForceLongPolling: true } : {}),
      });
    } catch (e: any) {
      if (__DEV__) {
        console.warn(
          '[Firebase] persistent Firestore cache unavailable; falling back to memory/getFirestore()',
          e?.message,
        );
      }
      try {
        return initializeFirestore(app, {
          localCache: memoryLocalCache(),
          ...(webFirestoreLongPollForced ? { experimentalForceLongPolling: true } : {}),
        });
      } catch {
        return getFirestore(app);
      }
    }
  }
  if (!nativeFirestoreDisableForceLongPolling) {
    try {
      return initializeFirestore(app, {
        experimentalForceLongPolling: true,
      });
    } catch (e: any) {
      if (__DEV__) {
        console.warn(
          '[Firebase] initializeFirestore(long polling) unavailable; falling back to getFirestore()',
          e?.message,
        );
      }
      return getFirestore(app);
    }
  }
  return getFirestore(app);
})();
export const storage = getStorage(app);

const parseHostPort = (value?: string) => {
  if (!value) return null;
  // Accept forms like "192.168.2.137:8080" or "localhost:8080"
  const [host, portStr] = value.split(':');
  const port = Number(portStr);
  if (!host || Number.isNaN(port)) return null;
  return { host, port };
};

const inferLanHostFromExpo = (): string | null => {
  try {
    const candidates: Array<string | undefined> = [
      (Constants as any)?.expoConfig?.hostUri,
      (Constants as any)?.manifest?.debuggerHost,
      (Constants as any)?.manifest2?.extra?.expoClient?.debuggerHost,
      (Constants as any)?.manifest2?.extra?.expoGo?.debuggerHost,
    ];

    for (const raw of candidates) {
      if (!raw || typeof raw !== 'string') continue;
      const noScheme = raw.replace(/^[a-z]+:\/\//i, '');
      const hostPort = noScheme.split('/')[0];
      const host = hostPort.split(':')[0];
      if (host && host !== 'localhost' && host !== '127.0.0.1') {
        return host;
      }
    }
  } catch {
    // ignore
  }
  return null;
};

const deriveEmulatorHostPort = (
  explicit: { host: string; port: number } | null,
  fallbackPort: number,
): { host: string; port: number } => {
  if (explicit) return explicit;

  // If functions emulator host is configured, reuse its host for other emulators by default.
  const functionsHostPort =
    parseHostPort(process.env.EXPO_PUBLIC_FUNCTIONS_EMULATOR_HOST) ||
    parseHostPort(Constants.expoConfig?.extra?.functionsEmulatorHost);
  if (functionsHostPort?.host) {
    return { host: functionsHostPort.host, port: fallbackPort };
  }

  // Try to infer LAN IP from Expo dev server metadata (useful on physical devices)
  const inferredLanHost = Platform.OS === 'web' ? null : inferLanHostFromExpo();
  if (inferredLanHost) {
    return { host: inferredLanHost, port: fallbackPort };
  }

  // Android emulator can't reach host machine via localhost.
  if (Platform.OS === 'android') {
    return { host: '10.0.2.2', port: fallbackPort };
  }

  return { host: 'localhost', port: fallbackPort };
};

// Connect to emulators in development mode.
// initializeFirestore (above) configures persistence but doesn't freeze settings,
// so connectFirestoreEmulator still works before the first query.
if (__DEV__) {
  // Check if emulator is explicitly disabled in config (highest priority)
  const useFunctionsEmulatorConfig = Constants.expoConfig?.extra?.useFunctionsEmulator;
  const useAuthEmulatorConfig = (Constants.expoConfig?.extra as any)?.useAuthEmulator;
  const envVarExplicitlyDisabled =
    useFunctionsEmulatorConfig === undefined && process.env.EXPO_PUBLIC_USE_FUNCTIONS_EMULATOR === 'false';
  const emulatorExplicitlyDisabled =
    useFunctionsEmulatorConfig === false ||
    envVarExplicitlyDisabled;

  // Debug logging to help diagnose configuration issues
  console.log('[Firebase Config] useFunctionsEmulator from config:', useFunctionsEmulatorConfig);
  console.log('[Firebase Config] useAuthEmulator from config:', useAuthEmulatorConfig);
  console.log('[Firebase Config] EXPO_PUBLIC_USE_FUNCTIONS_EMULATOR env:', process.env.EXPO_PUBLIC_USE_FUNCTIONS_EMULATOR);
  console.log('[Firebase Config] EXPO_PUBLIC_USE_AUTH_EMULATOR env:', process.env.EXPO_PUBLIC_USE_AUTH_EMULATOR);
  console.log('[Firebase Config] emulatorExplicitlyDisabled:', emulatorExplicitlyDisabled);
  console.log('[Firebase Config] envVarExplicitlyDisabled:', envVarExplicitlyDisabled);
  console.log('[Firebase Config] Platform.OS:', Platform.OS);

  // IMPORTANT: For detection tasks, we use emulator Firestore when functions emulator is enabled
  // because emulator functions create tasks in emulator Firestore
  // The client must read from the same Firestore instance (emulator or production) as the functions

  // If explicitly disabled (config/env), never use emulators - exit early
  if (emulatorExplicitlyDisabled || envVarExplicitlyDisabled) {
    console.log('🚫 Firebase emulators DISABLED (useFunctionsEmulator: false)');
    console.log('🚫 Using PRODUCTION Firebase services (Firestore, Storage)');
  } else {
    // Check if we should use emulators
    // Config explicitly set to true takes precedence
    const useEmulatorFromConfig = useFunctionsEmulatorConfig === true;
    // Only check env var if config is undefined
    const useEmulatorFromEnv = useFunctionsEmulatorConfig === undefined && process.env.EXPO_PUBLIC_USE_FUNCTIONS_EMULATOR === 'true';
    // IMPORTANT: Default to production even in dev.
    // Emulators must be explicitly enabled via app.json (extra.useFunctionsEmulator=true)
    // or EXPO_PUBLIC_USE_FUNCTIONS_EMULATOR=true.
    const shouldUseEmulator = useEmulatorFromConfig || useEmulatorFromEnv;

    if (shouldUseEmulator) {
      console.log('🔧 Attempting to connect to Firebase emulators...');
      console.log('   - useEmulatorFromConfig:', useEmulatorFromConfig);
      console.log('   - useEmulatorFromEnv:', useEmulatorFromEnv);

      // Prefer explicit host:port from env/config (works on device; localhost only in simulator)
      const firestoreHostPort = deriveEmulatorHostPort(
        parseHostPort(process.env.EXPO_PUBLIC_FIRESTORE_EMULATOR_HOST) ||
        parseHostPort(Constants.expoConfig?.extra?.firestoreEmulatorHost),
        8080,
      );
      const storageHostPort = deriveEmulatorHostPort(
        parseHostPort(process.env.EXPO_PUBLIC_STORAGE_EMULATOR_HOST) ||
        parseHostPort(Constants.expoConfig?.extra?.storageEmulatorHost),
        9199,
      );

      // Auth emulator is *opt-in* (production auth by default). This avoids "network-request-failed"
      // when other emulators are enabled but the Auth emulator isn't running/reachable.
      const useAuthEmulatorFromEnv = process.env.EXPO_PUBLIC_USE_AUTH_EMULATOR === 'true';
      const shouldUseAuthEmulator = useAuthEmulatorConfig === true || useAuthEmulatorFromEnv;
      console.log('   - shouldUseAuthEmulator:', shouldUseAuthEmulator);

      const authHostPort = shouldUseAuthEmulator
        ? (parseHostPort(process.env.EXPO_PUBLIC_AUTH_EMULATOR_HOST) ||
          parseHostPort(Constants.expoConfig?.extra?.authEmulatorHost) ||
          deriveEmulatorHostPort(null, 9099))
        : null;

      try {
        connectStorageEmulator(storage, storageHostPort.host, storageHostPort.port);
        console.log(`✅ Connected to Firebase Storage emulator at ${storageHostPort.host}:${storageHostPort.port}`);
      } catch (error) {
        if (error instanceof Error && !error.message.includes('already been called')) {
          console.warn('⚠️ Failed to connect to Firebase Storage emulator. Falling back to production.', error.message);
        }
      }

      try {
        connectFirestoreEmulator(db, firestoreHostPort.host, firestoreHostPort.port);
        console.log(`✅ Connected to Firebase Firestore emulator at ${firestoreHostPort.host}:${firestoreHostPort.port}`);
      } catch (error) {
        if (error instanceof Error && !error.message.includes('already been called')) {
          console.error('❌ Failed to connect to Firebase Firestore emulator:', error.message);
          console.error('   Falling back to production Firestore. If functions run in emulator, set useFunctionsEmulator=false.');
        }
      }

      if (authHostPort) {
        try {
          connectAuthEmulator(auth, `http://${authHostPort.host}:${authHostPort.port}`, { disableWarnings: true });
          console.log(`✅ Connected to Firebase Auth emulator at ${authHostPort.host}:${authHostPort.port}`);
        } catch (error) {
          if (error instanceof Error && !error.message.includes('already been called')) {
            console.warn('⚠️ Failed to connect to Firebase Auth emulator. Falling back to production auth.', error.message);
          }
        }
      } else {
        console.log('ℹ️ Auth emulator not enabled; using production Auth.');
      }
    } else {
      console.log('🚫 Firebase emulators not enabled (no explicit config/environment opt-in)');
      console.log('🚫 Using PRODUCTION Firebase services (Firestore, Storage)');
    }
  }
}

// Firestore persistence policy:
// - Web: persistentLocalCache (IndexedDB) when possible, memory fallback otherwise.
// - Native: default SDK cache behavior; app-level offline queues handle durable retries.
if (Platform.OS === 'web') {
  console.log(
    `✅ Firestore web: initializeFirestore with persistentLocalCache (memory fallback); forceLongPolling=${webFirestoreLongPollForced}`,
  );
} else if (!nativeFirestoreDisableForceLongPolling) {
  console.log(
    `✅ Firestore ${Platform.OS}: initializeFirestore experimentalForceLongPolling=true (disable via EXPO_PUBLIC_FIRESTORE_NATIVE_DISABLE_FORCE_LONG_POLLING)`,
  );
} else {
  console.log('ℹ️ Firestore native: using default SDK cache behavior');
}

// Firebase JS Performance on web auto-instruments interactions. React Native Web emits very long
// concatenated classNames (e.g. `.button.css-...r-1awozwy...`) that exceed Performance attribute limits
// and throw `FirebaseError: performance/invalid attribute value`, breaking unrelated flows (e.g. inbox).
// Opt in only when needed: EXPO_PUBLIC_ENABLE_FIREBASE_PERFORMANCE_WEB=true.
const enableFirebasePerformanceWeb =
  process.env.EXPO_PUBLIC_ENABLE_FIREBASE_PERFORMANCE_WEB === 'true' ||
  String((expoExtra as any)?.EXPO_PUBLIC_ENABLE_FIREBASE_PERFORMANCE_WEB || '').toLowerCase() === 'true';

let performance: ReturnType<typeof getPerformance> | null = null;
try {
  if (
    !__DEV__ &&
    Platform.OS === 'web' &&
    typeof getPerformance === 'function' &&
    enableFirebasePerformanceWeb
  ) {
    performance = getPerformance(app);
  }
} catch (error) {
  // Performance Monitoring may not be available in all environments.
  // Silently fail - app will work without it.
}
export { performance };

// Global error handler for Firestore internal assertion failures
// These are often benign and occur during cleanup or state transitions
// Works for both web and React Native
const suppressFirestoreInternalErrors = (error: any): boolean => {
  const errorMessage =
    (error?.message || error?.toString() || String(error || '')).toUpperCase();

  return (
    errorMessage.includes('FIRESTORE') &&
    errorMessage.includes('INTERNAL ASSERTION FAILED')
  );
};

let firestoreRuntimeHandlersInstalled = false;

export function installFirebaseRuntimeErrorHandlers(): void {
  if (firestoreRuntimeHandlersInstalled) {
    return;
  }
  firestoreRuntimeHandlersInstalled = true;

  // Web error handlers
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    const originalErrorHandler = window.onerror;
    window.onerror = function (message, source, lineno, colno, error) {
      if (typeof message === 'string' && suppressFirestoreInternalErrors(message)) {
        return true;
      }
      if (originalErrorHandler) {
        return originalErrorHandler.call(this, message, source, lineno, colno, error);
      }
      return false;
    };

    window.addEventListener('unhandledrejection', function (event) {
      if (suppressFirestoreInternalErrors(event.reason)) {
        event.preventDefault();
      }
    });
    return;
  }

  // React Native error handlers
  const ErrorUtils = (global as any).ErrorUtils;
  if (ErrorUtils) {
    const originalErrorHandler = ErrorUtils.getGlobalHandler?.();
    ErrorUtils.setGlobalHandler((error: Error, isFatal?: boolean) => {
      if (suppressFirestoreInternalErrors(error)) {
        return;
      }
      if (originalErrorHandler) {
        originalErrorHandler(error, isFatal);
      }
    });
  }
}


